import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as dashboardActions from "src/redux/slices/dashboardSlice";
import TableV2 from "src/components/ui/TableV2";
import { formatDateTime } from "src/utils/date-time";
import { currencyFormatter } from "src/utils/common";
import { ITenantRecentSaleOrder } from "src/types/dashboard.types";

interface RecentSalesCardProps {
  limit?: number;
  enabled?: boolean;
}

const RecentSalesCard: React.FC<RecentSalesCardProps> = ({
  limit = 5,
  enabled = true,
}) => {
  const dispatch = useAppDispatch();
  const { tenantRecentSales } = useAppSelector((state) => state.dashboard);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";

  useEffect(() => {
    if (!enabled) return;
    const params = isTenantAdmin || !user?._id ? { limit } : { limit, userId: user._id };
    dispatch(dashboardActions.getTenantRecentSalesRequest(params));
  }, [dispatch, enabled, isTenantAdmin, limit, user?._id]);

  if (!enabled) return null;

  const rows: ITenantRecentSaleOrder[] = tenantRecentSales.data?.data || [];

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-700">
          {isTenantAdmin ? "Recent Sales Orders" : "My Recent Sales Orders"}
        </h2>
        <span className="text-xs text-gray-400">Latest {limit}</span>
      </div>
      <TableV2
        columns={[
          {
            key: "orderId",
            header: "Order",
            accessor: ({ row }: { row: ITenantRecentSaleOrder }) => (
              <Link to={`/app/sale/${row._id}`} className="text-primary-600">
                {row.orderId}
              </Link>
            ),
          },
          {
            key: "customer",
            header: "Customer",
            accessor: ({ row }: { row: ITenantRecentSaleOrder }) =>
              row.customerId?.name || "-",
          },
          {
            key: "grandTotal",
            header: "Total",
            align: "right",
            accessor: ({ row }: { row: ITenantRecentSaleOrder }) =>
              currencyFormatter(row.grandTotal || 0),
          },
          {
            key: "createdAt",
            header: "Date",
            align: "center",
            accessor: ({ row }: { row: ITenantRecentSaleOrder }) =>
              formatDateTime(row.createdAt, "DD/MM/YYYY hh:mm:ss A"),
          },
        ]}
        data={rows}
      />
    </div>
  );
};

export default RecentSalesCard;
